// @ts-check
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { RUNWAY_SEG as SEG, HALL_CENTERS } from '../timeline.js';
import { computeScale } from '../scale-model.js';

/**
 * The gallery runway: one pinned, continuous exhibition corridor containing
 * three halls with deliberately different layout languages:
 *
 *   Hall I   colonnade — David / architecture / warm gold
 *   Hall II  filmstrip — Thinker / rainy city nights / cold blue
 *   Hall III breathing — Venus / nature / moon white
 *
 * Runway progress r ∈ [0, 1] drives two motion layers: the corridor itself
 * (track translateX) and each hall's interior (parallax / strip drift /
 * crossfade). Sculpture handoffs happen inside the dark gaps between halls.
 *
 * Segment boundaries come from ../timeline.js (single source of truth).
 */

/** Hall width / gap width in vw units. */
const HALL_VW = 100;
const GAP_VW = 60;

/** Filmstrip metrics, expressed at the 1440×900 reference.
 *
 *  They are written here as reference px and converted to the corridor's vw
 *  coordinate system through the page's shared scale model, because the
 *  corridor is a vw space: frame widths, the gap and every frame's left edge
 *  must all grow together or the strip tears itself apart. Hard-coding them in
 *  vw (24 / 4) meant pure width-proportional sizing — 346px frames on desktop
 *  but 94px thumbnails on a phone, which is what made the hall photographs
 *  look shrunken next to the rest of the page. */
const FRAME_WIDTHS_PX = [346, 288, 375, 317, 346];
/** A frame never takes more than this much of the viewport. */
const FRAME_CAP_VW = 62;
const FRAME_GAP_PX = 58;
/** Same cap the stylesheet applies to .filmstrip-track's gap. */
const FRAME_GAP_CAP_VW = 10;

/**
 * Model-derived filmstrip metrics for the current viewport, in vw.
 * @returns {{ widths: number[], gap: number }}
 */
function filmMetrics() {
  const vw = window.innerWidth;
  const scale = computeScale(vw, window.innerHeight);
  const toVw = (px) => (px / vw) * 100;
  return {
    widths: FRAME_WIDTHS_PX.map((px) => Math.min(FRAME_CAP_VW, toVw(px * scale))),
    gap: Math.min(FRAME_GAP_CAP_VW, toVw(FRAME_GAP_PX * scale)),
  };
}

/** @param {number} v @param {number} min @param {number} max */
function clamp(v, min = 0, max = 1) {
  return Math.max(min, Math.min(max, v));
}

/**
 * Local progress of a segment.
 * @param {number} r
 * @param {number[]} seg
 */
function localOf(r, seg) {
  return clamp((r - seg[0]) / (seg[1] - seg[0]));
}

export class GalleryRunway {
  /**
   * @param {HTMLElement} section - the #runway section
   * @param {Object[]} halls - halls data from exhibitions.json
   * @param {Object} [options={}]
   * @param {boolean} [options.isStatic=false] - static fallback (mobile / reduced motion)
   */
  constructor(section, halls, options = {}) {
    this.section = section;
    this.halls = halls;
    this.isStatic = options.isStatic ?? false;

    this.track = section.querySelector('.runway-track');
    if (!this.track) {
      console.error('GalleryRunway: missing .runway-track element');
      return;
    }

    /** @type {number} Raw scroll progress captured by ScrollTrigger; the eased
     *  value is applied by the central smooth-scroll loop in main.js. */
    this.targetProgress = 0;

    /** @type {Record<string, HTMLElement>} */
    this.hallEls = {};
    /** @type {Record<string, { index: number, captionZh?: HTMLElement|null, captionEn?: HTMLElement|null, frames?: HTMLElement[] }>} */
    this.hallState = {};

    this.build();

    if (!this.isStatic) {
      this.setupScrollTrigger();
      // Frame widths are model-derived, so they must be re-derived when the
      // viewport changes (orientation flip, window resize).
      this._onResize = () => this.relayout();
      window.addEventListener('resize', this._onResize, { passive: true });
    } else {
      this.section.classList.add('is-static');
      // Static mode: show first frame captions as-is, all frames visible via CSS.
    }
  }

  build() {
    this.track.innerHTML = '';

    this.halls.forEach((hall, i) => {
      if (i > 0) {
        this.track.appendChild(this.buildGap(hall));
      }
      const el = this.buildHall(hall);
      this.hallEls[hall.id] = el;
      this.track.appendChild(el);
    });
  }

  /**
   * Dark transition gap introducing the incoming hall.
   * @param {Object} hall
   */
  buildGap(hall) {
    const gap = document.createElement('div');
    gap.className = 'hall-gap';
    gap.setAttribute('aria-hidden', 'true');
    gap.innerHTML = `
      <p class="gap-numeral">${hall.numeral}</p>
      <p class="gap-name">${hall.title}</p>
      <p class="gap-line">${hall.subtitle}</p>
    `;
    return gap;
  }

  /**
   * @param {Object} hall
   */
  buildHall(hall) {
    const el = document.createElement('article');
    el.className = `hall hall-${hall.layout}`;
    el.id = `hall-${hall.id}`;
    el.dataset.hall = hall.id;
    el.setAttribute('aria-label', `${hall.title} 展厅`);

    const ghost = document.createElement('span');
    ghost.className = 'hall-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.textContent = hall.numeral;
    el.appendChild(ghost);

    if (hall.layout === 'colonnade') {
      el.appendChild(this.buildColonnade(hall));
      el.appendChild(this.buildHead(hall, 'tl'));
    } else if (hall.layout === 'filmstrip') {
      el.appendChild(this.buildFilmstrip(hall));
      el.appendChild(this.buildHead(hall, 'br'));
    } else if (hall.layout === 'breathing') {
      el.appendChild(this.buildBreathing(hall));
      el.appendChild(this.buildHead(hall, 'bc'));
    }

    return el;
  }

  /**
   * Hall header: series identity + (for filmstrip/breathing) live work caption.
   * @param {Object} hall
   * @param {'tl'|'br'|'bc'} pos - top-left / bottom-right / bottom-center
   */
  buildHead(hall, pos) {
    const head = document.createElement('header');
    head.className = `hall-head hall-head-${pos}`;

    const still = this.isStatic ? `<img class="hall-still" data-still="${hall.id}" alt="${hall.title} 雕塑" />` : '';

    if (hall.layout === 'colonnade') {
      head.innerHTML = `
        ${still}
        <p class="hall-kicker">HALL ${hall.numeral}</p>
        <h2 class="hall-title">${hall.title}</h2>
        <p class="hall-subtitle">${hall.subtitle}</p>
        <p class="hall-desc">${hall.desc}</p>
      `;
    } else {
      const first = hall.works[0];
      head.innerHTML = `
        ${still}
        <p class="hall-kicker">HALL ${hall.numeral}</p>
        <h2 class="hall-title">${hall.title}</h2>
        <p class="hall-subtitle">${hall.subtitle}</p>
        <p class="work-caption" aria-live="polite">
          <span class="work-caption-zh">${first.titleZh}</span>
          <span class="work-caption-en">${first.titleEn}</span>
        </p>
      `;
      this.hallState[hall.id] = {
        ...(this.hallState[hall.id] ?? { index: 0 }),
        captionZh: head.querySelector('.work-caption-zh'),
        captionEn: head.querySelector('.work-caption-en'),
      };
    }

    return head;
  }

  /**
   * Hall I — colonnade: static monumental grid, 1 large + 4 small,
   * differential parallax per slab.
   * @param {Object} hall
   */
  buildColonnade(hall) {
    const grid = document.createElement('div');
    grid.className = 'colonnade-grid';

    const speeds = [0.22, 0.5, 0.38, 0.62, 0.34];
    /** @type {HTMLElement[]} */
    const frames = [];

    hall.works.forEach((work, i) => {
      const figure = document.createElement('figure');
      figure.className = `colonnade-figure ${i === 0 ? 'colonnade-main' : 'colonnade-side'}`;
      figure.dataset.speed = String(speeds[i] ?? 0.4);
      figure.setAttribute('tabindex', '0');
      figure.setAttribute('role', 'button');
      figure.setAttribute('aria-label', `查看 ${work.titleZh} · ${work.titleEn}`);

      const img = this.buildImage(work, i < 2);
      figure.appendChild(img);

      // Each figure gets its own caption — hidden by default, shown when the
      // figure receives .is-active from setActiveFrame.
      const caption = document.createElement('figcaption');
      caption.className = 'work-caption';
      caption.innerHTML = `
        <span class="work-caption-zh">${work.titleZh}</span>
        <span class="work-caption-en">${work.titleEn}</span>
      `;
      figure.appendChild(caption);

      const openHandler = () => this.openLightbox(work, hall.works, i);
      figure.addEventListener('click', openHandler);
      figure.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openHandler();
        }
      });
      grid.appendChild(figure);
      frames.push(figure);
    });

    // Register frames so setActiveFrame toggles is-active per figure.
    this.hallState[hall.id] = {
      ...(this.hallState[hall.id] ?? { index: 0 }),
      frames,
    };

    this.colonnadeFigures = /** @type {HTMLElement[]} */ (
      Array.from(grid.querySelectorAll('[data-speed]'))
    );
    return grid;
  }

  /**
   * Hall II — filmstrip: lateral strip of uneven frames drifting with scroll;
   * the frame nearest the reading zone activates and owns the caption.
   * @param {Object} hall
   */
  buildFilmstrip(hall) {
    const frame = document.createElement('div');
    frame.className = 'filmstrip-frame';

    const strip = document.createElement('div');
    strip.className = 'filmstrip-track';

    const tilts = [-1.2, 0.8, -0.6, 1.1, -0.9];
    const { widths, gap } = filmMetrics();

    // Pre-calculate frame left-edges (vw) for transform math.
    /** @type {number[]} */
    const framePositions = [0];
    for (let i = 1; i < hall.works.length; i++) {
      framePositions.push(framePositions[i - 1] + widths[(i - 1) % widths.length] + gap);
    }
    this._filmFramePositions = framePositions;

    const frames = hall.works.map((work, i) => {
      const figure = document.createElement('figure');
      figure.className = 'film-frame';
      figure.style.setProperty('--tilt', `${tilts[i % tilts.length]}deg`);
      figure.style.width = `${widths[i % widths.length]}vw`;
      figure.setAttribute('tabindex', '0');
      figure.setAttribute('role', 'button');
      figure.setAttribute('aria-label', `查看 ${work.titleZh} · ${work.titleEn}`);
      figure.appendChild(this.buildImage(work, false));
      const openHandler = () => this.openLightbox(work, hall.works, i);
      figure.addEventListener('click', openHandler);
      figure.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openHandler();
        }
      });
      strip.appendChild(figure);
      return figure;
    });

    frame.appendChild(strip);
    this.filmstripTrack = strip;
    this.hallState[hall.id] = {
      ...(this.hallState[hall.id] ?? { index: 0 }),
      frames,
    };
    // Note: caption refs are attached in buildHead which runs after this.
    return frame;
  }

  /**
   * Hall III — breathing: one large image at a time, slow crossfade +
   * gentle Ken Burns, generous negative space.
   * @param {Object} hall
   */
  buildBreathing(hall) {
    const stage = document.createElement('div');
    stage.className = 'breath-stage';

    const frames = hall.works.map((work, i) => {
      const figure = document.createElement('figure');
      figure.className = `breath-frame${i === 0 ? ' is-active' : ''}`;
      figure.setAttribute('tabindex', '0');
      figure.setAttribute('role', 'button');
      figure.setAttribute('aria-label', `查看 ${work.titleZh} · ${work.titleEn}`);
      figure.appendChild(this.buildImage(work, i === 0));
      const openHandler = () => this.openLightbox(work, hall.works, i);
      figure.addEventListener('click', openHandler);
      figure.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openHandler();
        }
      });
      stage.appendChild(figure);
      return figure;
    });

    this.hallState[hall.id] = {
      ...(this.hallState[hall.id] ?? { index: 0 }),
      frames,
    };
    return stage;
  }

  /**
   * @param {Object} work
   * @param {boolean} eager
   */
  buildImage(work, eager) {
    const img = document.createElement('img');
    img.src = `${import.meta.env.BASE_URL}${work.src.startsWith('/') ? work.src.slice(1) : work.src}`;
    img.alt = work.titleZh;
    img.loading = eager ? 'eager' : 'lazy';
    img.fetchPriority = eager ? 'high' : 'auto';
    img.decoding = 'async';
    img.className = 'gallery-work-image';

    const focusX = Number.isFinite(work.focusX) ? work.focusX : 50;
    const focusY = Number.isFinite(work.focusY) ? work.focusY : 50;
    img.style.objectPosition = `${focusX}% ${focusY}%`;

    // Let mixed portrait/landscape film frames inherit the photograph's real
    // proportions. The image still uses cover for the tiny differences caused
    // by borders, but the frame no longer forces a landscape into a portrait.
    const updateAspect = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const aspect = img.naturalWidth / img.naturalHeight;
      const frame = img.parentElement;
      frame?.style.setProperty('--work-aspect', `${img.naturalWidth} / ${img.naturalHeight}`);
      frame?.style.setProperty('--work-aspect-number', String(aspect));

      if (!this.isStatic && frame?.classList.contains('film-frame')) this.relayout();
    };

    img.addEventListener('load', () => requestAnimationFrame(updateAspect), { once: true });
    if (img.complete) requestAnimationFrame(updateAspect);
    return img;
  }

  /**
   * @param {Object} work
   * @param {Object[]} [works=[]] — hall works array for prev/next
   * @param {number} [index=0] — position within works array
   */
  openLightbox(work, works = [], index = 0) {
    window.dispatchEvent(
      new CustomEvent('gallery:openLightbox', {
        detail: {
          work,
          works,
          index,
          src: work.src,
          title: `${work.titleZh} · ${work.titleEn}`,
          alt: work.titleZh,
        },
      })
    );
  }

  setupScrollTrigger() {
    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top top',
      end: '+=450%',
      pin: true,
      // No scrub — we only need the pin and raw progress. The central smooth
      // loop in main.js applies an eased version to the track transform.
      onUpdate: (self) => {
        this.targetProgress = self.progress;
      },
    });
  }

  /**
   * Drives corridor + hall interiors from runway progress.
   * @param {number} r
   */
  update(r) {
    if (!this.track) return;
    this._lastProgress = r;

    // Corridor position.
    const x = this.trackX(r);
    this.track.style.transform = `translate3d(${x}vw, 0, 0)`;

    // Hall I — differential parallax + active frame caption sync.
    const l1 = localOf(r, SEG.hall1);
    if (this.colonnadeFigures) {
      // Amplitude, in px at the extremes. Halved when this hall has a reserve:
      // on a portrait viewport the grid is fitted into the band above the
      // statue, and the reference ±43px swing would walk the bottom row over
      // the marble's crown. Half of it keeps the drift legible and inside the
      // band. Read from the DOM each frame so it tracks the reserve being
      // measured (or dropped) without a second copy of the rule here.
      const wallWidth = this.colonnadeFigures[0]?.parentElement?.clientWidth ?? 800;
      const amp = 140 * wallWidth / 800;
      this.colonnadeFigures.forEach((fig) => {
        const speed = Number(fig.dataset.speed ?? 0.4);
        fig.style.transform = `translateY(${(l1 - 0.5) * speed * amp}px)`;
      });
      // Cycle through works as the user scrolls through Hall I.
      const n1 = this.halls[0].works.length;
      this.setActiveFrame(this.halls[0].id, Math.min(Math.floor(l1 * n1), n1 - 1));
    }

    // Hall II — strip drift + active frame.
    const l2 = localOf(r, SEG.hall2);
    if (this.filmstripTrack && this._filmFramePositions) {
      const n = this.halls[1].works.length;
      // Use actual cumulative frame positions instead of constant step.
      const lastPos = this._filmFramePositions[n - 1];
      // Reading zone offset: position the active frame at ~30vw from left edge.
      const readingZone = 30;
      const x2 = readingZone - l2 * lastPos;
      this.filmstripTrack.style.transform = `translate3d(${x2}vw, 0, 0)`;
      this.setActiveFrame(this.halls[1].id, Math.round(l2 * (n - 1)));
    }

    // Hall III — crossfade + active frame.
    const l3 = localOf(r, SEG.hall3);
    const n3 = this.halls[2].works.length;
    this.setActiveFrame(this.halls[2].id, Math.min(Math.floor(l3 * n3), n3 - 1));

    // Coda — dim the last hall as Venus recedes.
    const codaLocal = localOf(r, SEG.coda);
    this.section.classList.toggle('is-coda', r > SEG.coda[0]);
    const venusEl = this.hallEls[this.halls[2].id];
    if (venusEl) {
      venusEl.style.opacity = String(1 - codaLocal * 0.7);
    }
  }

  /**
   * Re-derives the filmstrip metrics after a viewport change.
   *
   * The rest of the corridor is vw-based and reshapes itself, but the frame
   * widths come from the scale model (reference px × ratio → vw), so they have
   * to be recomputed or a phone rotated to landscape keeps phone-sized frames.
   */
  relayout() {
    const frames = this.hallState[this.halls[1]?.id]?.frames;
    if (!frames || frames.length === 0) return;

    const { widths: fallbackWidths, gap } = filmMetrics();
    const scale = computeScale(window.innerWidth, window.innerHeight);
    const rowHeight = Math.min(window.innerHeight * 0.42, 350 * scale);
    const rhythm = [1, 0.78, 1.05, 0.72, 0.9, 1, 0.82, 1.05];
    const widths = frames.map((frame, i) => {
      const img = frame.querySelector('img');
      const aspect = img?.naturalWidth && img?.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : fallbackWidths[i % fallbackWidths.length] / 28;
      return Math.min(52, Math.max(12, ((rowHeight * rhythm[i % rhythm.length] * aspect) / window.innerWidth) * 100));
    });
    /** @type {number[]} */
    const positions = [0];
    for (let i = 1; i < frames.length; i++) {
      positions.push(positions[i - 1] + widths[i - 1] + gap);
    }
    this._filmFramePositions = positions;
    frames.forEach((frame, i) => {
      frame.style.width = `${widths[i]}vw`;
    });
    if (this._lastProgress !== undefined) this.update(this._lastProgress);
  }

  /**
   * Corridor translateX in vw for runway progress r.
   * @param {number} r
   */
  trackX(r) {
    const step = HALL_VW + GAP_VW;
    if (r < SEG.move1[0]) return 0;
    if (r < SEG.move1[1]) {
      return -step * localOf(r, SEG.move1);
    }
    if (r < SEG.move2[0]) return -step;
    if (r < SEG.move2[1]) {
      return -step - step * localOf(r, SEG.move2);
    }
    return -step * 2;
  }

  /**
   * Marks a frame active inside a hall and syncs the live caption.
   * @param {string} hallId
   * @param {number} index
   */
  setActiveFrame(hallId, index) {
    const state = this.hallState[hallId];
    const hall = this.halls.find((h) => h.id === hallId);
    if (!state || !hall) return;

    const n = hall.works.length;
    const clamped = Math.max(0, Math.min(n - 1, index));
    // Skip only when both the active index and the DOM classes are known-good.
    if (clamped === state.index && state.synced && state.syncedClamped === clamped) return;

    state.index = clamped;
    state.synced = true;
    state.syncedClamped = clamped;

    // Always reconcile the DOM classes (cheap, and guards against any
    // out-of-sync state from re-renders or re-entrant updates).
    state.frames?.forEach((frameEl, i) => {
      frameEl.classList.toggle('is-active', i === clamped);
    });

    const work = hall.works[clamped];
    if (state.captionZh) state.captionZh.textContent = work.titleZh;
    if (state.captionEn) state.captionEn.textContent = work.titleEn;
  }

  /**
   * Scroll Y (px) that centers a hall — used by chapter rail / hero cards.
   * @param {string} hallId
   */
  getJumpScrollY(hallId) {
    const hallIndex = this.halls.findIndex((h) => h.id === hallId);
    const r = HALL_CENTERS[hallIndex] ?? 0;

    if (this.scrollTrigger) {
      return this.scrollTrigger.start + r * (this.scrollTrigger.end - this.scrollTrigger.start);
    }

    const el = this.hallEls[hallId];
    if (!el) return 0;
    return el.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.1;
  }

  destroy() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
    }
  }
}
